import Decimal from "decimal.js";
import type { Product } from "@/lib/products";
import type { CartItem } from "@/components/cart/types";
import { OrderTypes } from "@/lib/supabase/enums";
import { getRemainingStock } from "./catalog-filters";

export interface StorefrontCartLine {
  item: CartItem;
  product: Product | undefined;
  /** Base or variant price plus the chosen options, per unit. */
  unitPrice: number;
  lineTotal: number;
  optionNames: string[];
  /** The most this line may hold given the stock; undefined = unlimited. */
  maxQuantity: number | undefined;
}

type ProductOption =
  Product["productOptionSubgroups"][number]["productOptions"][number];

/**
 * An option's price, the way the server prices it: a product-type option by
 * the order type's price list, a plain option by its own price.
 */
function getOptionPrice(option: ProductOption, orderType: OrderTypes): number {
  if (option.takeAwayPrice !== undefined || option.deliveryPrice !== undefined) {
    return orderType === OrderTypes.pickup
      ? (option.takeAwayPrice ?? 0)
      : (option.deliveryPrice ?? 0);
  }
  return option.price;
}

function getSelectedOptions(
  product: Product | undefined,
  selectedOptions: Record<string, string> | undefined
): ProductOption[] {
  if (!product || !selectedOptions) return [];
  return product.productOptionSubgroups.flatMap((subgroup) => {
    const value = selectedOptions[subgroup.id];
    if (!value) return [];
    const ids = value.split(",").map((id) => id.trim());
    return subgroup.productOptions.filter((option) => ids.includes(option.id));
  });
}

/**
 * Cart items joined with their catalogue product: prices, option names and the
 * stock ceiling of each line. A line's ceiling is the product's stock minus
 * what the cart's other lines of the same product already hold.
 */
export function buildCartLines(
  items: CartItem[],
  productsById: Map<string, Product>,
  orderType: OrderTypes
): StorefrontCartLine[] {
  const quantityByProductId = getQuantityByProductId(items);

  return items.map((item) => {
    const product = productsById.get(item.productId);
    const options = getSelectedOptions(product, item.selectedOptions);
    const unitPrice = new Decimal(item.price)
      .plus(
        options.reduce(
          (sum, option) => sum.plus(getOptionPrice(option, orderType)),
          new Decimal(0)
        )
      )
      .toNumber();
    const otherLinesQuantity =
      (quantityByProductId.get(item.productId) ?? 0) - item.quantity;

    return {
      item,
      product,
      unitPrice,
      lineTotal: new Decimal(unitPrice).mul(item.quantity).toNumber(),
      optionNames: options.map((option) => option.name),
      maxQuantity: product
        ? getRemainingStock(product, otherLinesQuantity)
        : undefined,
    };
  });
}

export function getQuantityByProductId(items: CartItem[]): Map<string, number> {
  const quantities = new Map<string, number>();
  for (const item of items) {
    quantities.set(
      item.productId,
      (quantities.get(item.productId) ?? 0) + item.quantity
    );
  }
  return quantities;
}

export function getCartSubtotal(lines: StorefrontCartLine[]): number {
  return lines
    .reduce((sum, line) => sum.plus(line.lineTotal), new Decimal(0))
    .toNumber();
}
